import {
  CheckBox,
  CheckBoxOutlineBlank,
  CheckBoxOutlined,
  Restore,
  Save,
} from '@mui/icons-material';
import {
  Box, Button, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import * as React from 'react';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import Grid from '@mui/material/Grid';
import { objectsEqual } from 'shared/utils';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import { getBuildingPath } from 'shared/routes/building';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { GridWrapper } from 'shared/components/data-display/GridFormatter';

/**
 * Component that displays a data grid of buildings with their NAS configurations
 * @param {Array} buildings - Array of building objects to display
 * @param {Object} [nasProfile=null] - Current NAS profile configuration
 * @param {Function} onSave - Callback function when changes are saved
 */
export default function NasDataGrid({ buildings, nasProfile = null, onSave }) {
  const gridData = buildings.map((building) => ({
    name: building.name,
    id: building.id,
    nas_name: building.dot1x ? building.dot1x.nas_profile.nas.nas_name : 'No building Nas',
    ip: building.dot1x ? building.dot1x.nas_profile.ip : 'No building VTR',
    dot1x: building.dot1x || {},
  }));
  const [dirtyBuildings, setDirtyBuildings] = useState([]);
  const [dirty, setDirty] = useState(false);
  const theme = useTheme();
  const compactedColumns = useMediaQuery(theme.breakpoints.down('sm'));
  const { id, ip, nas } = nasProfile || {};
  const { nas_name: nasName } = nas || {};

  useEffect(() => {
    if (dirtyBuildings.length > 0) {
      setDirty(true);
    } else {
      setDirty(false);
    }
  }, [dirtyBuildings.length]);

  const handleRevert = useCallback(() => {
    setDirtyBuildings([]);
    setDirty(false);
  }, []);

  const renderIcon = useCallback((row) => {
    if (dirtyBuildings.some((curr) => curr.id === row.id)) {
      return <CheckBox />;
    } if (row.nas_name === nasName && row.ip === ip) {
      return <CheckBoxOutlined />;
    }
    return <CheckBoxOutlineBlank />;
  }, [ip, nasName, dirtyBuildings]);

  const handleSave = () => {
    onSave(dirtyBuildings);
    setDirtyBuildings([]);
    setDirty(false);
  };

  const handleCheckClick = useCallback((row) => {
    if (dirtyBuildings.some((curr) => curr.id === row.id)) {
      const updatedDirtyBuildings = dirtyBuildings.filter((curr) => !objectsEqual(curr, row));
      setDirtyBuildings(updatedDirtyBuildings);
    } else {
      setDirtyBuildings([...dirtyBuildings, row]);
    }
  }, [dirtyBuildings]);

  const cellLink = (params) => (
    <a href={getBuildingPath(params.row.id)}>
      {params.row.name}
    </a>
  );

  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: 'Building',
      width: compactedColumns ? 150 : 350,
      renderCell: cellLink,
    },
    {
      field: 'nas_name',
      headerName: 'Current Main Router',
      type: 'string',
      width: 150,
    },
    {
      field: 'ip',
      headerName: 'Current VTR',
      type: 'string',
      width: 150,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Set Default',
      editable: true,
      getActions: ({ row }) => [
        <GridActionsCellItem
          icon={renderIcon(row)}
          label="Set to Default"
          onClick={() => handleCheckClick(row)}
          color="inherit"
          disabled={(row.nas_name === nasName && row.ip === ip)
            || (!ip && !nasName)}
        />,
      ],
    },
  ], [compactedColumns, ip, nasName, handleCheckClick, renderIcon]);

  return (
    <Box>
      <Box sx={{
        width: '100%',
      }}
      >
        <GridWrapper size={12} title="Selected Defaults">
          <Grid container size={{ xs: 12 }} sx={{ justifyContent: 'space-between', marginBottom: 2 }}>
            <Grid size={{ xs: 6 }}>
              <DataFormatter
                descriptions={[{
                  label: 'Main Router',
                  key: 'nas_name',
                  formatter: (n) => (n.id ? `${n.nas_name} (${n.description})` : ''),
                  readOnly: true,
                }]}
                fallback="No VTR was selected for configuration"
                data={nas || null}
              />
            </Grid>
            <Grid size={{ xs: 5 }}>
              <DataFormatter
                descriptions={[{
                  label: 'VTR',
                  key: 'ip',
                  readOnly: true,
                }]}
                data={nasProfile || undefined}
              />
            </Grid>
          </Grid>
        </GridWrapper>
        <GridWrapper size={12} title="Configure Buildings">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              mb: 2,
            }}
          >
            <Typography color="text.secondary">
              Total Results&nbsp;
              {buildings.length}
            </Typography>
          </Box>
          {buildings.length === 0 ? (
            <Typography>This complex has no buildings</Typography>
          ) : (
            <DataGrid
              rows={gridData}
              columns={columns}
              experimentalFeatures={{ clipboardPaste: true }}
              hideFooter
            />
          )}
          <Box
            style={{ marginBottom: 8 }}
            sx={{
              display: 'flex',
              justifyContent: 'end',
              gap: 1,
              py: 2,
            }}
          >
            {dirty && (
              <Button
                onClick={() => handleRevert()}
                startIcon={<Restore />}
                variant={VARIANTS.outlined}
                color={BUTTON_COLORS.error}
              >
                Reset
              </Button>
            )}
            <Button
              onClick={() => handleSave()}
              startIcon={<Save />}
              variant={VARIANTS.contained}
              disabled={!dirty || !id}
            >
              Save
            </Button>
          </Box>
        </GridWrapper>
      </Box>
    </Box>
  );
}
